import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import AreaShell from '../../chrome/AreaShell';
import useAuthenticatedApi from '../../../Authentication/components/hooks/useAuthenticatedApi';
import { ConfirmDialog, SectionCard } from '../../components/forms';
import { TCard, TEmpty, TSkeleton } from '../../components/table';
import { Frow, useToast } from '../../components/roster';
import './admin-ops.css';

/*
 * The API host that answered this poll: hostname + disk usage, every 10s.
 * Behind a load balancer each poll may land on a different host.
 * Temp-image cleanup uses the POST (classic used a GET with no confirm).
 */

const POLL_MS = 10000;

interface Usage {
    hostname?: string;
    diskUsage?: string;
}

const clock = (d: Date) => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' });

export default function AdminSystemHealthV3() {
    const api = useAuthenticatedApi();
    const toast = useToast();

    const [usage, setUsage] = useState<Usage | null>(null);
    const [state, setState] = useState<'loading' | 'ok' | 'failed'>('loading');
    const [stale, setStale] = useState(false);
    const [checkedAt, setCheckedAt] = useState<Date | null>(null);
    const inFlight = useRef(false);
    const hadData = useRef(false);

    const [confirming, setConfirming] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [clearError, setClearError] = useState('');

    const poll = useCallback(async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        try {
            const r = await api.get('/telemetry/server-resource-usage');
            hadData.current = true;
            setUsage(r.data ?? {});
            setCheckedAt(new Date());
            setStale(false);
            setState('ok');
        } catch {
            // keep the last good reading on screen
            if (hadData.current) setStale(true);
            else setState('failed');
        } finally {
            inFlight.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        poll();
        const id = window.setInterval(() => {
            if (document.visibilityState !== 'hidden') poll();
        }, POLL_MS);
        return () => window.clearInterval(id);
    }, [poll]);

    const clearTemp = async () => {
        if (clearing) return;
        setClearing(true);
        setClearError('');
        try {
            await api.post('/telemetry/clear-temp-directory-images');
            toast.show('Temp images deleted.');
        } catch {
            setClearError('Couldn’t delete temp images. Try again.');
        } finally {
            setClearing(false);
        }
    };

    return (
        <AreaShell area="admin" tab="system-health" sub="API server resources, refreshed every 10 seconds">
            <div className="ao-page">
                {state === 'loading' && (
                    <TCard>
                        <TSkeleton rows={2} />
                    </TCard>
                )}
                {state === 'failed' && (
                    <TCard>
                        <TEmpty
                            message="Couldn’t reach the telemetry endpoint."
                            actionLabel="Retry"
                            onAction={() => {
                                setState('loading');
                                poll();
                            }}
                        />
                    </TCard>
                )}
                {state === 'ok' && (
                    <SectionCard
                        title="API server"
                        note={
                            stale
                                ? `Last refresh failed · showing ${checkedAt ? clock(checkedAt) : 'last reading'}`
                                : checkedAt
                                  ? `Checked ${clock(checkedAt)}`
                                  : undefined
                        }
                    >
                        <Frow label="Hostname">
                            <span className="ao-mono">{usage?.hostname || '—'}</span>
                        </Frow>
                        <Frow label="Disk usage">
                            <span className="ao-mono">{usage?.diskUsage || '—'}</span>
                        </Frow>
                    </SectionCard>
                )}

                <SectionCard title="Temp images" note="Working files left over from processing">
                    <div className="ao-job">
                        <span className="ao-job-t">
                            <span className="ao-job-n">Delete temp images</span>
                            <span className="ao-job-s">
                                Clears the temp image folder on the API server that answers. Files still being processed can be lost.
                            </span>
                        </span>
                        <button
                            type="button"
                            className="tb-btn danger-soft"
                            disabled={clearing}
                            onClick={() => setConfirming(true)}
                        >
                            {clearing ? 'Deleting…' : 'Delete temp images…'}
                        </button>
                    </div>
                    {clearError && (
                        <p className="ao-note err" role="alert" style={{ marginTop: 10 }}>
                            {clearError}
                        </p>
                    )}
                </SectionCard>
            </div>

            <ConfirmDialog
                open={confirming}
                title="Delete all temp images?"
                body="This empties the temp image folder on the API server. Any photo still being processed there can be lost, and this can’t be undone."
                actionLabel="Delete temp images"
                onConfirm={clearTemp}
                onClose={() => setConfirming(false)}
            />
            {toast.node}
        </AreaShell>
    );
}
